import React, {useCallback} from 'react';
import {DEFAULT_NULL_VALUE} from 'common/constants';
import Dropdown, {DropdownOptions, DropdownProps, isDropdownObjectOption} from './Dropdown';
import {useController, useFormContext} from 'react-hook-form';
import {SetValueOptions} from 'components/hook-form/helpers';
import {useMemoRef} from 'common/hooks/useMemoRef';
import {useRegisterLabel} from 'common/context/FormContext';

type Props = {
   name: string;
   options: DropdownOptions;
   required?: boolean;
   defaultRenderValue?: string;
   className?: string;
   // @deprecated use disabled prop instead
   enabled?: boolean;
} & Partial<DropdownProps>;

const HookFormDropdown = (props: Props) => {
   const {
      name,
      options,
      defaultRenderValue = DEFAULT_NULL_VALUE,
      enabled = true,
      onChange,
      disabled,
      ...otherProps
   } = props;
   const label = props.label || props.placeholder;

   const {setValue} = useFormContext();
   useRegisterLabel(name, otherProps.placeholder || (otherProps.label as string));

   const {
      field: {onChange: fieldOnChange, onBlur, value, ref},
      fieldState: {error: _error},
   } = useController({
      name,
      // rules: {required: true},
      // defaultValue: '',
   });

   const disable = disabled || !enabled;
   //TODO: Consider to be deleted because duplicate default behavior
   const renderValue = useCallback(
      (val) => {
         const selectedOption = options?.find((option) =>
            isDropdownObjectOption(option) ? option.id === val : option === val
         );
         if (!selectedOption) {
            return defaultRenderValue;
         }
         return isDropdownObjectOption(selectedOption) ? selectedOption.name : selectedOption;
      },
      [options, defaultRenderValue]
   );

   const _ref = useMemoRef(ref);
   const _onChange = React.useCallback(
      (ev, child) => {
         onChange && onChange(ev, child);
         if (!ev.target.value) {
            return setValue(name, null, SetValueOptions);
         }
         fieldOnChange(ev);
      },
      [name, fieldOnChange, onChange, setValue]
   );

   return (
      <Dropdown
         // onChange={onChange} // send value to hook form
         onBlur={onBlur} // notify when input is touched/blur
         value={value} // input value
         name={name} // send down the input name
         inputRef={_ref} // send input ref, so we can focus on input when error appear
         displayEmpty
         renderValue={renderValue}
         error={_error?.message}
         disabled={disable}
         label={label}
         options={options}
         {...otherProps}
         onChange={_onChange}
      />
   );
};

export default React.memo(HookFormDropdown);
